import { jwtDecode } from 'jwt-decode';
import type { Profile } from 'oidc-client';
import { DEFAULT_IDP, PUBLIC_COMPUTER_CLAIM, USER_STATE_STORAGE_KEY } from '../helpers/constants';
import { isPublicComputerClaim } from '../helpers/public-computer-claim';
import {
  beginPublicSession,
  clearStoredSessionLocally,
  purgeDurableCredentials,
  readSessionItem,
  removeSessionItem,
  wasSignedOutElsewhere,
  writeSessionItem,
} from '../helpers/session-scoped-storage';

export { USER_STATE_STORAGE_KEY };
export const USER_STATE_UPDATED_EVENT = 'user-state-updated';

type Auth = boolean;
type currentSchema = string;
type schemas = string;
type controlledUsername = string;

type User = {
  preferred_username?: string;
  schemas?: schemas; // list of schema codes separaed by comma
  currentSchema?: currentSchema; // schema code
  superuser: string;
  controlledUsername?: controlledUsername;
};
type Tokens = {
  id_token: string;
  access_token: string;
  expires_at: number;
  refresh_token?: string;
  auth_time?: number;
};
type LoginStatus = 'idp' | 'login' | 'schema' | 'done';
export type UserState = {
  auth: Auth;
  user?: User;
  tokens?: Tokens;
  loginStatus?: LoginStatus;
  idp?: string;
  isDefaultIdp?: boolean;
};
type LoginJwt = {
  token: string;
  schema: string;
  idp?: string;
};

export const UserActionTypes = {
  UpdateUser: 0,
  UpdateAuth: 1,
  UpdateLoginStatus: 2,
  UpdateCurrentSchema: 3,
  UpdateSchemas: 4,
  UpdateControlledUsername: 5,
  UpdateLoginJwt: 6,
  Reset: 7,
} as const;
export type UserActionTypes = (typeof UserActionTypes)[keyof typeof UserActionTypes];

export interface UpdateUser {
  type: typeof UserActionTypes.UpdateUser;
  payload: UserState;
}
export interface Reset {
  type: typeof UserActionTypes.Reset;
}
export interface UpdateSchemas {
  type: typeof UserActionTypes.UpdateSchemas;
  payload: schemas;
}
export interface UpdateAuth {
  type: typeof UserActionTypes.UpdateAuth;
  payload: Auth;
}
export interface UpdateLoginStatus {
  type: typeof UserActionTypes.UpdateLoginStatus;
  payload: LoginStatus;
}
export interface UpdateCurrentSchema {
  type: typeof UserActionTypes.UpdateCurrentSchema;
  payload: currentSchema;
}
export interface UpdateControlledUsername {
  type: typeof UserActionTypes.UpdateControlledUsername;
  payload: controlledUsername | undefined;
}
export interface UpdateLoginJwt {
  type: typeof UserActionTypes.UpdateLoginJwt;
  payload: LoginJwt;
}
export type UserReducerActions =
  | UpdateUser
  | Reset
  | UpdateAuth
  | UpdateLoginStatus
  | UpdateCurrentSchema
  | UpdateSchemas
  | UpdateControlledUsername
  | UpdateLoginJwt;

export const initialState: UserState = {
  auth: false,
};

export const safeParseUserState = (raw: string | null): UserState => {
  if (!raw) return initialState;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && typeof parsed.auth === 'boolean') {
      return parsed as UserState;
    }
  } catch {
    // Invalid JSON
  }
  return initialState;
};

/**
 * Reads the persisted session, discarding it first if it was signed out while this
 * tab was not running — a tab the browser discarded misses the sign-out broadcast
 * and reloads on reactivation, so this is where it finds out.
 */
export const loadPersistedUserState = (): UserState => {
  if (wasSignedOutElsewhere()) {
    // Drop only this tab's stale copy, then read what's left: the durable store may hold a
    // newer sibling login, which a reset here would overwrite via the persist effect.
    clearStoredSessionLocally();
  }
  return safeParseUserState(readSessionItem(USER_STATE_STORAGE_KEY));
};

export const userReducer = (state: UserState, action: UserReducerActions) => {
  switch (action.type) {
    case UserActionTypes.UpdateUser: {
      if (action.payload.auth === false) {
        return {
          ...initialState,
          ...action.payload,
        };
      }
      return {
        ...state,
        ...action.payload,
      };
    }
    case UserActionTypes.UpdateSchemas: {
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            schemas: action.payload,
          },
        };
      }
      return state;
    }
    case UserActionTypes.UpdateAuth:
      return {
        ...state,
        auth: action.payload,
      };
    case UserActionTypes.UpdateLoginStatus:
      return {
        ...state,
        loginStatus: action.payload,
      };
    case UserActionTypes.UpdateCurrentSchema:
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            currentSchema: action.payload,
          },
        };
      }
      return state;
    case UserActionTypes.UpdateControlledUsername:
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            controlledUsername: action.payload,
          },
        };
      }
      return state;
    case UserActionTypes.UpdateLoginJwt: {
      const { idp, schema, token } = action.payload;
      const _idp = idp ?? DEFAULT_IDP;
      const decodedToken: Profile = jwtDecode(token);
      // Token handoff, not an OIDC login: isolate to sessionStorage but don't migrate stale
      // durable entries (a leftover oidc user would be adopted as the previous user) nor set the
      // shared "active" cookie (which would suppress a sibling app's gate).
      if (isPublicComputerClaim(decodedToken[PUBLIC_COMPUTER_CLAIM])) {
        // Fail closed: if the session can't be isolated, persisting this authenticated state
        // durably is the leak this prevents. Drop the login — purging durable credentials,
        // since the skipped migrateDurable:false branch would have removed a prior user's.
        if (!beginPublicSession({ migrateDurable: false, markSession: false })) {
          purgeDurableCredentials();
          removeSessionItem(USER_STATE_STORAGE_KEY);
          removeSessionItem('userId');
          return { ...initialState };
        }
      }
      // Persist opaque user ID for Sentry (matches LoginCallback path).
      if (decodedToken.sub) {
        writeSessionItem('userId', decodedToken.sub);
      } else {
        removeSessionItem('userId');
      }
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          currentSchema: schema,
          schemas: decodedToken['custom:schema'],
          preferred_username: decodedToken.preferred_username,
          superuser: decodedToken['custom:superuser'],
        },
        tokens: {
          ...state.tokens,
          id_token: token,
          expires_at: decodedToken.exp,
          access_token: '', // we don't have an access token
        },
        loginStatus: 'done',
        idp: _idp,
        isDefaultIdp: _idp === DEFAULT_IDP,
      } as UserState;
    }
    case UserActionTypes.Reset:
      removeSessionItem(USER_STATE_STORAGE_KEY);
      removeSessionItem('userId');
      return {
        ...initialState,
      };
    default:
      throw new Error('Unexpected action');
  }
};

/** Persist user state to this session's storage and notify listeners. */
export const updateUserState = (userState: UserState) => {
  writeSessionItem(USER_STATE_STORAGE_KEY, JSON.stringify(userState));
  window.dispatchEvent(new CustomEvent(USER_STATE_UPDATED_EVENT));
};
